import React, { useState, useEffect, useRef } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';

const QuickActionToolbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeAction, setActiveAction] = useState(null);
  const [formData, setFormData] = useState({});
  const menuRef = useRef(null);
  const modalRef = useRef(null);

  const quickActions = [
    {
      id: 'new-transaction',
      label: 'Nova Transação',
      icon: 'Plus',
      description: 'Registrar receita ou despesa'
    },
    {
      id: 'record-payment',
      label: 'Registrar Pagamento',
      icon: 'CreditCard',
      description: 'Confirmar pagamento recebido'
    },
    {
      id: 'add-expense',
      label: 'Adicionar Despesa',
      icon: 'Receipt',
      description: 'Lançar nova despesa'
    },
    {
      id: 'new-client',
      label: 'Novo Cliente',
      icon: 'UserPlus',
      description: 'Cadastrar novo cliente'
    }
  ];

  const transactionTypes = [
    { value: 'income', label: 'Receita' },
    { value: 'expense', label: 'Despesa' }
  ];

  const paymentMethods = [
    { value: 'cash', label: 'Dinheiro' },
    { value: 'credit', label: 'Cartão de Crédito' },
    { value: 'debit', label: 'Cartão de Débito' },
    { value: 'transfer', label: 'Transferência' },
    { value: 'pix', label: 'PIX' }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef?.current && !menuRef?.current?.contains(event?.target)) {
        setIsMenuOpen(false);
      }
      if (modalRef?.current && !modalRef?.current?.contains(event?.target)) {
        handleCloseModal();
      }
    };

    if (isMenuOpen || isModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, isModalOpen]);

  const handleToggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleActionClick = (action) => {
    setActiveAction(action);
    setIsMenuOpen(false);
    setIsModalOpen(true);
    setFormData({});
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setActiveAction(null);
    setFormData({});
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    console.log('Form submitted:', { action: activeAction?.id, data: formData });
    handleCloseModal();
  };

  const renderModalContent = () => {
    if (!activeAction) return null;

    switch (activeAction?.id) {
      case 'new-transaction':
        return (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Select
              label="Tipo de Transação"
              options={transactionTypes}
              value={formData?.type}
              onChange={(value) => handleInputChange('type', value)}
              required
            />
            <Input
              label="Descrição"
              type="text"
              placeholder="Digite a descrição"
              value={formData?.description || ''}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              required
            />
            <Input
              label="Valor"
              type="number"
              placeholder="0,00"
              value={formData?.amount || ''}
              onChange={(e) => handleInputChange('amount', e?.target?.value)}
              required
            />
            <Input
              label="Data"
              type="date"
              value={formData?.date || ''}
              onChange={(e) => handleInputChange('date', e?.target?.value)}
              required
            />
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseModal} fullWidth>
                Cancelar
              </Button>
              <Button type="submit" fullWidth>
                Salvar
              </Button>
            </div>
          </form>
        );

      case 'record-payment':
        return (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Cliente"
              type="text"
              placeholder="Nome do cliente"
              value={formData?.client || ''}
              onChange={(e) => handleInputChange('client', e?.target?.value)}
              required
            />
            <Input
              label="Valor Recebido"
              type="number"
              placeholder="0,00"
              value={formData?.amount || ''}
              onChange={(e) => handleInputChange('amount', e?.target?.value)}
              required
            />
            <Select
              label="Método de Pagamento"
              options={paymentMethods}
              value={formData?.method}
              onChange={(value) => handleInputChange('method', value)}
              required
            />
            <Input
              label="Data do Pagamento"
              type="date"
              value={formData?.date || ''}
              onChange={(e) => handleInputChange('date', e?.target?.value)}
              required
            />
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseModal} fullWidth>
                Cancelar
              </Button>
              <Button type="submit" fullWidth>
                Confirmar
              </Button>
            </div>
          </form>
        );

      case 'add-expense':
        return (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Descrição da Despesa"
              type="text"
              placeholder="Digite a descrição"
              value={formData?.description || ''}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              required
            />
            <Input
              label="Valor"
              type="number"
              placeholder="0,00"
              value={formData?.amount || ''}
              onChange={(e) => handleInputChange('amount', e?.target?.value)}
              required
            />
            <Input
              label="Categoria"
              type="text"
              placeholder="Ex: Aluguel, Salários"
              value={formData?.category || ''}
              onChange={(e) => handleInputChange('category', e?.target?.value)}
              required
            />
            <Input
              label="Data"
              type="date"
              value={formData?.date || ''}
              onChange={(e) => handleInputChange('date', e?.target?.value)}
              required
            />
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseModal} fullWidth>
                Cancelar
              </Button>
              <Button type="submit" fullWidth>
                Adicionar
              </Button>
            </div>
          </form>
        );

      case 'new-client':
        return (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Nome do Cliente"
              type="text"
              placeholder="Nome completo ou razão social"
              value={formData?.name || ''}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              required
            />
            <Input
              label="Email"
              type="email"
              placeholder="email@exemplo.com"
              value={formData?.email || ''}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              required
            />
            <Input
              label="Telefone"
              type="tel"
              placeholder="(00) 00000-0000"
              value={formData?.phone || ''}
              onChange={(e) => handleInputChange('phone', e?.target?.value)}
              required
            />
            <Input
              label="CPF/CNPJ"
              type="text"
              placeholder="000.000.000-00"
              value={formData?.document || ''}
              onChange={(e) => handleInputChange('document', e?.target?.value)}
              required
            />
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseModal} fullWidth>
                Cancelar
              </Button>
              <Button type="submit" fullWidth>
                Cadastrar
              </Button>
            </div>
          </form>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <button
        className="quick-action-fab"
        onClick={handleToggleMenu}
        aria-label="Ações rápidas"
      >
        <Icon name={isMenuOpen ? 'X' : 'Zap'} size={24} />
      </button>
      {isMenuOpen && (
        <div ref={menuRef} className="quick-action-menu">
          {quickActions?.map((action) => (
            <div
              key={action?.id}
              className="quick-action-item"
              onClick={() => handleActionClick(action)}
            >
              <Icon name={action?.icon} size={20} />
              <div>
                <div className="text-sm font-medium">{action?.label}</div>
                <div className="text-xs text-muted-foreground">{action?.description}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[1020] flex items-center justify-center p-4">
          <div
            ref={modalRef}
            className="bg-card rounded-lg shadow-xl w-full max-w-md p-6"
            style={{ animation: 'slideUp 300ms ease-out' }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">
                {activeAction?.label}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-1 rounded-md hover:bg-muted transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            {renderModalContent()}
          </div>
        </div>
      )}
    </>
  );
};

export default QuickActionToolbar;